import React, { useState, useEffect } from "react"
import {
  CompositeScoreTier,
  useCreateCompositeScoreTierMutation,
  useUpdateCompositeScoreTierMutation,
  useCreateCompositeScoreBucketMutation,
  useGetMeasuresQuery,
} from "../../../app/services/meseeknaApiV2"

type TierFormModalProps = {
  tier: CompositeScoreTier | null // null = create mode
  scoreSetId: number
  isOpen: boolean
  onClose: () => void
  onSave: () => void
}

const TierFormModal: React.FC<TierFormModalProps> = ({
  tier,
  scoreSetId,
  isOpen,
  onClose,
  onSave,
}) => {
  const isCreating = tier === null
  const [name, setName] = useState("")
  const [color, setColor] = useState("#6366f1") // Default indigo
  const [outputScore, setOutputScore] = useState<number>(0)
  const [measureId, setMeasureId] = useState<number | "">("")
  const [minScore, setMinScore] = useState<number>(0)
  const [maxScore, setMaxScore] = useState<number>(100)
  const [error, setError] = useState<string | null>(null)

  const [createTier, { isLoading: isCreatingTier }] = useCreateCompositeScoreTierMutation()
  const [updateTier, { isLoading: isUpdating }] = useUpdateCompositeScoreTierMutation()
  const [createBucket, { isLoading: isCreatingBucket }] = useCreateCompositeScoreBucketMutation()

  const { data: measuresData, isLoading: measuresLoading } = useGetMeasuresQuery()
  const measures = measuresData?.data || []

  const isLoading = isCreatingTier || isUpdating || isCreatingBucket

  useEffect(() => {
    if (tier) {
      setName(tier.name || "")
      setColor(tier.color || "#6366f1")
      setOutputScore(tier.output_score ?? 0)
    } else {
      setName("")
      setColor("#6366f1")
      setOutputScore(0)
      setMeasureId("")
      setMinScore(0)
      setMaxScore(100)
    }
    setError(null)
  }, [tier, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!name.trim()) {
      setError("Name is required")
      return
    }

    if (!color.trim()) {
      setError("Color is required")
      return
    }

    // Validate hex color format
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/
    if (!hexColorRegex.test(color)) {
      setError("Color must be a valid hex code (e.g., #FF5733)")
      return
    }

    // Measure is required when creating
    if (isCreating && measureId === "") {
      setError("Please select a measure for this tier")
      return
    }

    // Validate score range when creating
    if (isCreating) {
      if (minScore < 0 || minScore > 100) {
        setError("Min score must be between 0 and 100")
        return
      }
      if (maxScore < 0 || maxScore > 100) {
        setError("Max score must be between 0 and 100")
        return
      }
      if (minScore > maxScore) {
        setError("Min score cannot be greater than max score")
        return
      }
    }

    try {
      if (isCreating) {
        // Create the tier first
        const tierResult = await createTier({
          name: name.trim(),
          color: color.trim(),
          output_score: outputScore,
          composite_score_set_id: scoreSetId,
        }).unwrap()

        // Then create the bucket for the selected measure
        const newTierId = tierResult.data?.insertId
        if (newTierId && measureId !== "") {
          await createBucket({
            measure_id: measureId as number,
            min_score: minScore,
            max_score: maxScore,
            composite_score_tier_id: newTierId,
          }).unwrap()
        }
      } else if (tier) {
        await updateTier({
          id: tier.id,
          name: name.trim(),
          color: color.trim(),
          output_score: outputScore,
        }).unwrap()
      }
      onSave()
    } catch (err) {
      console.error("Failed to save tier:", err)
      setError("Failed to save tier. Please try again.")
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-[55]">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-medium text-gray-900">
            {isCreating ? "Create Tier" : "Edit Tier"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 cursor-pointer"
          >
            <span className="text-2xl">&times;</span>
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Measure selection (only when creating) */}
          {isCreating && (
            <div className="mb-4">
              <label
                htmlFor="measure"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Measure <span className="text-red-500">*</span>
              </label>
              <select
                id="measure"
                value={measureId}
                onChange={(e) =>
                  setMeasureId(e.target.value ? parseInt(e.target.value) : "")
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={measuresLoading}
              >
                <option value="">
                  {measuresLoading ? "Loading measures..." : "Select a measure"}
                </option>
                {measures.map((measure) => (
                  <option key={measure.id} value={measure.id}>
                    {measure.measureName}
                    {measure.measureCode ? ` (${measure.measureCode})` : ""}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                The tier will be associated with this measure
              </p>
            </div>
          )}

          <div className="mb-4">
            <label
              htmlFor="tierName"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="tierName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="e.g., Novice, Intermediate, Expert"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="tierColor"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Color <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                id="tierColorPicker"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-10 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                id="tierColor"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="#FF5733"
              />
            </div>
          </div>

          <div className="mb-4">
            <label
              htmlFor="outputScore"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Output Score <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              id="outputScore"
              value={outputScore}
              onChange={(e) => setOutputScore(parseInt(e.target.value) || 0)}
              min={0}
              max={100}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              The score value assigned when a measure falls into this tier (0-100)
            </p>
          </div>

          {/* Score range (only when creating) */}
          {isCreating && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Initial Score Range <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="minScore"
                    className="block text-xs text-gray-500 mb-1"
                  >
                    Min Score
                  </label>
                  <input
                    type="number"
                    id="minScore"
                    value={minScore}
                    onChange={(e) => setMinScore(parseInt(e.target.value) || 0)}
                    min={0}
                    max={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label
                    htmlFor="maxScore"
                    className="block text-xs text-gray-500 mb-1"
                  >
                    Max Score
                  </label>
                  <input
                    type="number"
                    id="maxScore"
                    value={maxScore}
                    onChange={(e) => setMaxScore(parseInt(e.target.value) || 0)}
                    min={0}
                    max={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Scores within this range will be assigned to this tier
              </p>
            </div>
          )}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md text-sm font-medium hover:bg-gray-300 cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 cursor-pointer disabled:opacity-50"
            >
              {isLoading ? "Saving..." : isCreating ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default TierFormModal
